import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Image as ImageIcon, Send } from 'lucide-react';
import {
  THEMES,
  photoFromFile,
  setTheme,
  themeBackground,
  useTheme,
  type ThemeId,
} from '../lib/theme.ts';
// The preview's bubbles are the thread's own, so what it promises is what the
// conversation shows.
import '../components/MessageThread.css';
import './SettingsScreen.css';
import './ThemeScreen.css';

/** The badge on the card that is on. */
function SelectedBadge() {
  return (
    <span className="theme-card-check">
      <Check size={12} strokeWidth={3} />
    </span>
  );
}

/**
 * Picking a theme. The whole app is repainted the moment a card is tapped
 * rather than on «Готово» — the screen behind this one is the thing being
 * changed, and a preview that only promises is worth less than the change
 * itself. «Отмена» is what makes that safe: it puts back whatever was on when
 * the screen opened.
 */
export function ThemeScreen() {
  const navigate = useNavigate();
  const theme = useTheme();
  // Captured once, so the trip through half a dozen cards still has one thing
  // to undo to.
  const [opened] = useState(theme);
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);

  /**
   * Whether this screen was left through «Готово».
   *
   * Every other way out is a dismissal and has to mean «Отмена». That is what a
   * header with those two buttons in it promises, and it is not what leaving
   * used to do: Android's back button and iOS's edge swipe both go through the
   * browser's history rather than through either button, so the last card
   * tapped simply stayed. Applying on tap is deliberate — the screen behind is
   * the thing being changed and a preview that only promises is worth less —
   * but it turns «зайти посмотреть, как выглядит» into a decision nobody made,
   * which is how somebody who has always used «Обычную» ends up in «Облаках».
   */
  const kept = useRef(false);

  useEffect(
    () => () => {
      if (!kept.current) setTheme(opened.id, opened.photo);
    },
    [opened],
  );

  function choose(id: ThemeId) {
    setMessage(null);
    setTheme(id);
  }

  function cancel() {
    // The unmount below would put it back anyway; done here as well so the app
    // is already wearing it when the settings screen draws.
    setTheme(opened.id, opened.photo);
    navigate('/settings');
  }

  function done() {
    kept.current = true;
    navigate('/settings');
  }

  async function pickPhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared right away, so picking the same file twice still fires a change.
    event.target.value = '';
    if (!file) return;
    setMessage(null);
    try {
      const photo = await photoFromFile(file);
      // The pick applies either way; only its survival of a reload is in doubt.
      if (!setTheme('custom', photo)) {
        setMessage('Фото не поместилось в память браузера — оно сбросится после перезагрузки');
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Не удалось открыть фото');
    }
  }

  return (
    <div className="app-frame settings-screen theme-screen">
      <header className="theme-header">
        <button type="button" className="theme-header-action" onClick={cancel}>
          Отмена
        </button>
        <h2 className="theme-heading">Оформление</h2>
        <button
          type="button"
          className="theme-header-action theme-header-done"
          onClick={done}
        >
          Готово
        </button>
      </header>

      {/* The point of a preview this size: judging text against the wallpaper
          before living with it, which a 84px card cannot show. Inert — every
          tap on this screen belongs to the rail below. */}
      <div className="theme-preview" style={{ background: themeBackground(theme) }}>
        <div className="theme-preview-stack" aria-hidden="true">
          <div className="thread-day">
            <span>Сегодня</span>
          </div>

          <div className="theme-preview-row theme-preview-row-out">
            <div className="bubble bubble-out theme-preview-bubble theme-preview-bubble-out">
              Я в своём познании настолько преисполнился…
              <span className="theme-preview-meta">21:42 ✓✓</span>
            </div>
          </div>

          <div className="theme-preview-row">
            <span className="theme-preview-avatar" />
            <div className="bubble bubble-in theme-preview-bubble">
              <span className="theme-preview-sender">Лиза</span>
              Пожалуйста, не начинай
              <span className="theme-preview-meta">21:42</span>
            </div>
          </div>

          <div className="theme-preview-composer">
            <span className="theme-preview-field">Сообщение</span>
            <span className="theme-preview-send">
              <Send size={16} />
            </span>
          </div>
        </div>
      </div>

      {/* Horizontal rather than a grid: the cards are portraits of a screen,
          and a screen shown 84px wide has to keep its proportions. */}
      <div className="theme-rail">
        {THEMES.map((known) => {
          const selected = theme.id === known.id;
          return (
            <button
              key={known.id}
              type="button"
              className="theme-card-slot"
              onClick={() => choose(known.id)}
              aria-pressed={selected}
            >
              {/* Each card wears the theme it offers, so it is drawn by the
                  same tokens the app would be — a card can never fall out of
                  step with what tapping it does. */}
              <span
                data-theme={known.id}
                className={`theme-card${selected ? ' theme-card-selected' : ''}`}
              >
                <span className="theme-card-out" />
                <span className="theme-card-in" />
                {selected && <SelectedBadge />}
              </span>
              <span className="theme-card-name">{known.name}</span>
            </button>
          );
        })}

        <button
          type="button"
          className="theme-card-slot"
          onClick={() => fileInput.current?.click()}
          aria-pressed={theme.id === 'custom'}
        >
          <span
            className={`theme-card theme-card-pick${
              theme.id === 'custom' ? ' theme-card-selected' : ''
            }`}
            style={theme.photo ? { background: themeBackground(theme) } : undefined}
          >
            {!theme.photo && <ImageIcon size={22} />}
            {theme.id === 'custom' && <SelectedBadge />}
          </span>
          <span className="theme-card-name">Своё фото</span>
        </button>

        <input
          ref={fileInput}
          className="theme-file"
          type="file"
          accept="image/*"
          onChange={(event) => void pickPhoto(event)}
        />
      </div>

      {message && <div className="theme-message">{message}</div>}
    </div>
  );
}
